
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { saveFamilyProfile, getFamilyProfile } from "@/utils/family";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { useAuth } from "@/contexts/auth";
import { FamilyFormProvider } from "./family/FamilyFormContext";
import { ParentsSection } from "./family/ParentsSection";
import { SiblingsSection } from "./family/SiblingsSection";
import { GrandparentsSection } from "./family/GrandparentsSection";

export function FamilyProfileForm({ onComplete }: { onComplete?: () => void }) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [initialData, setInitialData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      const profile = await getFamilyProfile();
      if (profile) {
        setInitialData({
          motherName: profile.motherName || "",
          fatherName: profile.fatherName || "",
          siblings: profile.siblings || [],
          grandparents: profile.grandparents || []
        });
      }
    };
    
    loadProfile();
  }, []);

  const handleSave = async (formContext: any) => {
    if (!formContext.motherName && !formContext.fatherName && 
        formContext.siblings.length === 0 && formContext.grandparents.length === 0) {
      toast.error(language === 'ja' 
        ? "少なくとも一人の家族情報を入力してください" 
        : "Please enter at least one family member's information");
      return;
    }

    setLoading(true);
    
    try {
      // Validate sibling data
      const validSiblings = formContext.siblings.filter((sibling: any) => sibling.name.trim() !== "");
      
      // Validate grandparent data
      const validGrandparents = formContext.grandparents.filter((grandparent: any) => grandparent.name.trim() !== "");

      await saveFamilyProfile({
        motherName: formContext.motherName,
        fatherName: formContext.fatherName,
        siblings: validSiblings,
        grandparents: validGrandparents
      });

      toast.success(language === 'ja' 
        ? "家族のプロフィールが保存されました" 
        : "Family profile saved successfully!");
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error("Error saving family profile:", error);
      toast.error(language === 'ja' ? "保存中にエラーが発生しました" : "An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  // Wait for initial data to load
  if (initialData === null) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <FamilyFormProvider initialData={initialData}>
      {(contextValue) => (
        <Card className="w-full max-w-md mx-auto border-0 shadow-none">
          <CardContent className="space-y-4 pt-4">
            <ParentsSection />
            
            <Separator className="my-4" />
            
            <SiblingsSection />
            
            <Separator className="my-4" />
            
            <GrandparentsSection />
          </CardContent>
          <CardFooter>
            <Button 
              onClick={() => handleSave(contextValue)}
              className="w-full"
              disabled={loading}
            >
              {loading 
                ? (language === 'ja' ? "保存中..." : "Saving...") 
                : t('common.save', language)}
            </Button>
          </CardFooter>
        </Card>
      )}
    </FamilyFormProvider>
  );
}
